import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../components/global/layout';
import ApiClient from '../methods/api/apiClient';
import loader from '../methods/loader';
import { toast } from 'react-toastify';
import { decryptId, encryptId } from '../components/common/Encryption/encryption';
import datepipeModel from '../models/datepipemodel';
import environment from '../environment';
import axios from 'axios';
import Modal from '../components/common/Modal';
import GroupHistory from './GroupHistory';

const TroikaAssignments = () => {
  const { eventid } = useParams<{ eventid: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);

  const [id, setId] = useState<string | null>(null);
  const [event, setEvent] = useState<any>(null);
  const [groupDetails, setGroupDetails] = useState<any>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [labels, setLabels] = useState<Record<number, any[]>>({});
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [moveOpen, setMoveOpen] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyName, setHistoryName] = useState<string>('');

  const groupData = groupDetails || (typeof event?.groupId === 'object' ? event?.groupId : null) || {};
  const resolvedGroupId = groupData?._id || groupDetails?._id || event?.groupId || user?.groupId?._id;
  const connectMeetLabel = groupData?.connectMeetLabel || user?.connectMeetLabel || 'ConnectMeet';

  const getUserRoleInEventGroup = () => {
    const eventGroupId = groupData?._id || event?.groupId;
    if (!eventGroupId || !user?.groups) return null;
    const group = user.groups.find((g: any) => {
      const gid = g.groupId?._id || g.groupId;
      return gid === eventGroupId && g.status === 'active';
    });
    if (!group) return null;
    return group.customerRole?._id || group.customerRole;
  };

  const canManage = (() => {
    if (user?.isGuest) return false;
    const roleId = getUserRoleInEventGroup();
    if (!roleId) return false;
    if (environment.groupLeaderRoleIds.includes(roleId)) return true;
    if (environment.connectmeetmanagerRoleId === roleId) return true;
    return false;
  })();

  useEffect(() => {
    if (eventid) {
      decryptId(eventid).then((dec: string) => setId(dec));
    }
  }, [eventid]);

  const loadData = useCallback(() => {
    if (!id) return;
    setLoading(true);

    const p1 = new Promise<void>((resolve) => {
      ApiClient.get(`api/event/group/list?eventId=${id}`).then((res: any) => {
        if (res.success) {
          setGroups(res.data || []);
          if (res.data?.[0]?.eventsDetails) {
            setEvent(res.data[0].eventsDetails);
          }
          if (res.data?.[0]?.groupDetails) {
            setGroupDetails(res.data[0].groupDetails);
          }
        }
        resolve();
      });
    });

    const p2 = new Promise<void>((resolve) => {
      ApiClient.get(`api/troika-labels?eventId=${id}`).then((res: any) => {
        if (res.success) {
          setLabels(res.labels || {});
          setStats(res.stats || null);
        }
        resolve();
      });
    });

    Promise.all([p1, p2]).then(() => setLoading(false));
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!id) return;
    ApiClient.get(`api/event/detail?id=${id}`).then((res: any) => {
      if (res.success && res.data) setEvent(res.data);
    });
  }, [id]);

  const handleRegenerate = async () => {
    if (!id) return;
    loader(true);
    ApiClient.get(`api/event/groups?eventId=${id}`).then((res: any) => {
      loader(false);
      if (res.success) {
        toast.success('Groups regenerated');
        loadData();
      } else {
        toast.error(res.message || 'Failed to regenerate');
      }
    });
  };

  const handleExport = async () => {
    if (!id) return;
    try {
      const res = await axios({
        method: 'get',
        url: `${environment.api}api/export/event-group?eventId=${id}`,
      });
      const binaryString = atob(res.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const anchor = document.createElement('a');
      anchor.href = window.URL.createObjectURL(blob);
      const evtTitle = (event?.title || 'GroupsDetail').replace(/[^a-zA-Z0-9 ]/g, '').trim();
      const evtDate = event?.date ? new Date(event.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-') : '';
      let evtTime = '';
      if (event?.date) {
        const d = new Date(event.date);
        const tz = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timeParts = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz }).replace(/\s/g, '');
        const tzAbbr = new Date('2020-01-15T12:00:00Z').toLocaleTimeString('en-US', { timeZoneName: 'short', timeZone: tz }).split(' ').pop();
        evtTime = `_${timeParts}${tzAbbr}`;
      }
      anchor.download = `${evtTitle}${evtDate ? '_' + evtDate : ''}${evtTime}.xlsx`;
      anchor.click();
    } catch {
      toast.error('Failed to export');
    }
  };

  const handleShareWithMembers = () => {
    if (!id) return;
    if (!resolvedGroupId) {
      toast.error('Group ID is missing');
      return;
    }
    loader(true);
    ApiClient.post('api/send-email/event-group', { eventId: id, groupId: resolvedGroupId }).then((res: any) => {
      loader(false);
      if (res.success) {
        toast.success(res.message);
      } else {
        toast.error(res.message || 'Failed to send emails');
      }
    });
  };

  const handleMove = (attendee: any, sourceGroup: any, targetConnectMeetId: string) => {
    setMoveOpen(null);
    const sourceCmId = String(sourceGroup._id?.ConnectMeetId || '');

    setGroups(prev => prev.map(g => {
      const gCmId = String(g._id?.ConnectMeetId || '');
      if (gCmId === sourceCmId) {
        return { ...g, attendees: (g.attendees || []).filter((a: any) => a.id !== attendee.id) };
      }
      if (gCmId === targetConnectMeetId) {
        return { ...g, attendees: [...(g.attendees || []), attendee] };
      }
      return g;
    }));

    const payload = {
      data: [{
        id: attendee.id,
        ConnectMeetId: targetConnectMeetId,
        attendeeId: attendee.attendeesDetails?._id,
        eventId: attendee.attendeesDetails?.eventId,
        groupId: attendee.attendeesDetails?.groupId
      }]
    };
    ApiClient.put('api/event/group/edit', payload).then((res: any) => {
      if (res.success) {
        toast.success(res.message);
        ApiClient.post('api/refresh-combinations', { eventId: id });
      } else {
        toast.error(res.message || 'Failed to move');
        loadData();
      }
    });
  };

  const openHistory = (userId: string, name: string) => {
    setHistoryName(name);
    setHistoryData(null);
    setHistoryModal(true);
    loader(true);
    ApiClient.get(`api/user/historic/data?userId=${userId}&groupId=${resolvedGroupId}&eventId=${id}`).then((res: any) => {
      loader(false);
      if (res.success) {
        setHistoryData(res.data);
      }
    });
  };

  const handleDragStart = (e: React.DragEvent, attendee: any, sourceGroup: any) => {
    setDraggedItem({ attendee, sourceGroup });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, groupCmId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGroupId(groupCmId);
  };

  const handleDrop = (e: React.DragEvent, targetGroup: any) => {
    e.preventDefault();
    setDragOverGroupId(null);
    if (!draggedItem) return;
    const { attendee, sourceGroup } = draggedItem;
    const targetCmId = String(targetGroup._id?.ConnectMeetId || '');
    const sourceCmId = String(sourceGroup._id?.ConnectMeetId || '');
    if (targetCmId === sourceCmId) return;
    handleMove(attendee, sourceGroup, targetCmId);
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverGroupId(null);
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const sortedGroups = [...groups]
    .filter(g => g.groupNo !== 'Not_interested')
    .sort((a, b) => {
      const aNo = parseInt(a.groupNo || a._id?.ConnectMeetId || '0');
      const bNo = parseInt(b.groupNo || b._id?.ConnectMeetId || '0');
      return aNo - bNo;
    });

  const declinedGroups = groups.filter(g => g.groupNo === 'Not_interested');
  const declinedAttendees = declinedGroups.flatMap(g => g.attendees || []);

  const notHonoredCount = stats ? stats.requestsNotHonored : 0;

  const getLabelStyle = (color: string) => {
    switch (color) {
      case 'green': return { bg: '#F0FDF4', badge: '#15803D' };
      case 'red':
      case 'amber': return { bg: '#FFF8EB', badge: '#A16207' };
      default: return { bg: '#F6F6F4', badge: '#52525B' };
    }
  };

  const eventDate = event?.date ? datepipeModel.formatDateToTimezone(event.date, event.timezone || user?.timezone) : '';

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 40px 48px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {/* Header */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={async () => {
                if (id) {
                  const enc = await encryptId(id);
                  navigate(`/event/detail/${enc}`);
                } else {
                  navigate(-1);
                }
              }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#C2410C', textDecoration: 'none', textAlign: 'left' }}
            >
              &larr; {event?.title || 'Back to event'}
            </button>
            <h1 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 32, fontWeight: 800 }}>{connectMeetLabel} assignments</h1>
            {canManage && (
              <span style={{ fontSize: 15, color: '#3F3F46' }}>
                Follow-up meetings from {eventDate}. Draft, nobody has been notified yet.
              </span>
            )}
            {!canManage && (
              <span style={{ fontSize: 15, color: '#3F3F46' }}>
                Follow-up meetings from {eventDate}.
              </span>
            )}
          </div>
          {canManage && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                onClick={handleRegenerate}
                style={{ height: 44, padding: '0 18px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#18181B', cursor: 'pointer' }}
              >
                Regenerate
              </button>
              <button
                onClick={handleExport}
                style={{ height: 44, padding: '0 18px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#18181B', cursor: 'pointer' }}
              >
                Export
              </button>
              <button
                onClick={handleShareWithMembers}
                style={{ height: 44, padding: '0 20px', borderRadius: 12, border: 'none', background: '#C2410C', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
              >
                Share with members
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards (leaders only) */}
        {canManage && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div style={{ background: '#FFFFFF', border: '1px solid #E4E4E0', borderRadius: 16, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#52525B' }}>{connectMeetLabel}s</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 26, fontWeight: 800 }}>
                {stats.totalTroikas} <span style={{ fontSize: 15, fontWeight: 600, color: '#52525B' }}>for {stats.totalPeople} people</span>
              </span>
            </div>
            <div style={{ background: '#FFFFFF', border: '1px solid #E4E4E0', borderRadius: 16, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#52525B' }}>Requests honored</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 26, fontWeight: 800 }}>
                {stats.requestsHonored} <span style={{ fontSize: 15, fontWeight: 600, color: '#52525B' }}>of {stats.totalRequests}</span>
              </span>
            </div>
            <div style={{ background: '#FFFFFF', border: '1px solid #E4E4E0', borderRadius: 16, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#52525B' }}>Repeat pairings</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 26, fontWeight: 800 }}>
                {stats.repeatPairings} <span style={{ fontSize: 15, fontWeight: 600, color: '#52525B' }}>within {stats.historyMonths} months</span>
              </span>
            </div>
          </div>
        )}

        {/* Warning Banner (leaders only) */}
        {canManage && notHonoredCount > 0 && (
          <div style={{ background: '#FFF8EB', border: '1px solid #F5D9A3', borderRadius: 16, padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A16207" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16.5v.01" />
            </svg>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#713F12' }}>
                {notHonoredCount} request{notHonoredCount !== 1 ? 's' : ''} couldn't be honored
              </span>
              <span style={{ fontSize: 14, lineHeight: 1.5, color: '#3F3F46' }}>
                Each {connectMeetLabel.toLowerCase()} below says why. Move people by hand if you want a different result, then send.
              </span>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#A1A1AA' }}>
            <div style={{ display: 'inline-block', width: 24, height: 24, border: '2px solid #D4D4D8', borderTopColor: '#C2410C', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 8 }} />
            <p style={{ margin: 0, fontSize: 14 }}>Loading assignments...</p>
          </div>
        )}

        {/* Group Assignment Declined */}
        {!loading && declinedGroups.length > 0 && declinedGroups.map((dg: any, dgIdx: number) => {
          const dgAttendees = dg.attendees || [];
          const dgCmId = String(dg._id?.ConnectMeetId || `declined-${dgIdx}`);
          if (dgAttendees.length === 0) return null;
          return (
            <article
              key={`declined-${dgIdx}`}
              onDragOver={canManage ? (e) => handleDragOver(e, dgCmId) : undefined}
              onDragLeave={canManage ? () => setDragOverGroupId(null) : undefined}
              onDrop={canManage ? (e) => handleDrop(e, dg) : undefined}
              style={{
                background: '#FFFFFF', border: dragOverGroupId === dgCmId ? '2px solid #C2410C' : '1px solid #E4E4E0',
                borderRadius: 18, padding: 18,
                display: 'flex', flexDirection: 'column', gap: 12,
                transition: 'border-color 0.15s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>Group Assignment Declined</h2>
                <span style={{ fontSize: 13, color: '#52525B' }}>{dgAttendees.length} {dgAttendees.length === 1 ? 'person' : 'people'}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {dgAttendees.map((att: any, attIdx: number) => {
                  const name = att.attendeesDetails?.fullName || att.attendeesDetails?.email || 'Member';
                  const attKey = att.id || `${dgCmId}-${attIdx}`;
                  const isMoveOpen = moveOpen === attKey;

                  return (
                    <div
                      key={attKey}
                      draggable={canManage}
                      onDragStart={canManage ? (e) => handleDragStart(e, att, dg) : undefined}
                      onDragEnd={canManage ? handleDragEnd : undefined}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, cursor: canManage ? 'grab' : 'default', position: 'relative' }}
                    >
                      <span style={{ width: 32, height: 32, borderRadius: 999, background: '#F4F4F5', color: '#3F3F46', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {getInitials(name)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{name}</span>
                        {att.attendeesDetails?.email && (
                          <span style={{ display: 'block', fontSize: 12, color: '#71717A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.attendeesDetails.email}</span>
                        )}
                      </div>
                      {canManage && (
                        <>
                          <button
                            type="button"
                            title="View history"
                            onClick={(e) => { e.stopPropagation(); openHistory(att.attendeesDetails?.userId, name); }}
                            style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #E4E4E0', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                          >
                            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                            </svg>
                          </button>
                          <div style={{ position: 'relative' }}>
                            <button
                              type="button"
                              title="Move member"
                              onClick={() => setMoveOpen(isMoveOpen ? null : attKey)}
                              style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #E4E4E0', background: isMoveOpen ? '#F4F4F5' : '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                            >
                              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M7 7h11l-3-3M17 17H6l3 3" />
                              </svg>
                            </button>
                            {isMoveOpen && (
                              <div style={{ position: 'absolute', right: 0, top: 40, zIndex: 50, background: '#FFFFFF', border: '1px solid #E4E4E0', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '6px 0', minWidth: 180 }}>
                                {sortedGroups.map((g: any, gi: number) => {
                                  const targetNo = parseInt(g.groupNo || g._id?.ConnectMeetId || `${gi + 1}`);
                                  return (
                                    <button
                                      key={g._id?.ConnectMeetId || gi}
                                      onClick={() => handleMove(att, dg, String(g._id?.ConnectMeetId))}
                                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'transparent', fontSize: 14, color: '#18181B', cursor: 'pointer' }}
                                      onMouseEnter={(e) => (e.currentTarget.style.background = '#F4F4F5')}
                                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                    >
                                      {connectMeetLabel} {targetNo}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}

        {/* Troika Cards Grid */}
        {!loading && sortedGroups.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {sortedGroups.map((group: any, idx: number) => {
              const gNo = parseInt(group.groupNo || group._id?.ConnectMeetId || `${idx + 1}`);
              const cmId = String(group._id?.ConnectMeetId || '');
              const attendees = group.attendees || [];
              const groupLabels = labels[gNo] || [];

              return (
                <article
                  key={cmId || idx}
                  onDragOver={canManage ? (e) => handleDragOver(e, cmId) : undefined}
                  onDragLeave={canManage ? () => setDragOverGroupId(null) : undefined}
                  onDrop={canManage ? (e) => handleDrop(e, group) : undefined}
                  style={{
                    background: '#FFFFFF',
                    border: dragOverGroupId === cmId ? '2px solid #C2410C' : '1px solid #E4E4E0',
                    borderRadius: 18, padding: 18,
                    display: 'flex', flexDirection: 'column', gap: 12,
                    transition: 'border-color 0.15s'
                  }}
                >
                  {/* Card Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h2 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 16, fontWeight: 700 }}>{connectMeetLabel} {gNo}</h2>
                    <span style={{ fontSize: 13, color: '#52525B' }}>{attendees.length} people</span>
                  </div>

                  {/* Members */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {attendees.map((att: any, attIdx: number) => {
                      const name = att.attendeesDetails?.fullName || att.attendeesDetails?.email || 'Member';
                      const attKey = att.id || `${cmId}-${attIdx}`;
                      const isMoveOpen = moveOpen === attKey;

                      return (
                        <div
                          key={attKey}
                          draggable={canManage}
                          onDragStart={canManage ? (e) => handleDragStart(e, att, group) : undefined}
                          onDragEnd={canManage ? handleDragEnd : undefined}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, cursor: canManage ? 'grab' : 'default', position: 'relative' }}
                        >
                          <span style={{ width: 32, height: 32, borderRadius: 999, background: '#F4F4F5', color: '#3F3F46', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {getInitials(name)}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{name}</span>
                            {att.attendeesDetails?.email && (
                              <span style={{ display: 'block', fontSize: 12, color: '#71717A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.attendeesDetails.email}</span>
                            )}
                          </div>
                          {canManage && (
                            <>
                              <button
                                type="button"
                                title="View history"
                                onClick={(e) => { e.stopPropagation(); openHistory(att.attendeesDetails?.userId, name); }}
                                style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #E4E4E0', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                              >
                                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                </svg>
                              </button>
                              <div style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  title="Move member"
                                  onClick={() => setMoveOpen(isMoveOpen ? null : attKey)}
                                  style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #E4E4E0', background: isMoveOpen ? '#F4F4F5' : '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                                >
                                  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M7 7h11l-3-3M17 17H6l3 3" />
                                  </svg>
                                </button>
                                {isMoveOpen && (
                                  <div style={{ position: 'absolute', right: 0, top: 40, zIndex: 50, background: '#FFFFFF', border: '1px solid #E4E4E0', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '6px 0', minWidth: 180 }}>
                                    {sortedGroups.filter(g => String(g._id?.ConnectMeetId) !== cmId).map((g: any, gi: number) => {
                                      const targetNo = parseInt(g.groupNo || g._id?.ConnectMeetId || `${gi + 1}`);
                                      return (
                                        <button
                                          key={g._id?.ConnectMeetId || gi}
                                          onClick={() => handleMove(att, group, String(g._id?.ConnectMeetId))}
                                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'transparent', fontSize: 14, color: '#18181B', cursor: 'pointer' }}
                                          onMouseEnter={(e) => (e.currentTarget.style.background = '#F4F4F5')}
                                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                        >
                                          {connectMeetLabel} {targetNo}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Labels (leaders only) */}
                  {canManage && groupLabels.map((label: any, lIdx: number) => {
                    const lstyle = getLabelStyle(label.color);
                    return (
                      <div key={lIdx} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px', borderRadius: 12, background: lstyle.bg }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: lstyle.badge }}>{label.heading}</span>
                        <span style={{ fontSize: 13, lineHeight: '1.45', color: '#3F3F46' }}>{label.text}</span>
                      </div>
                    );
                  })}
                </article>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && sortedGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#A1A1AA', background: '#FFFFFF', border: '1px solid #E4E4E0', borderRadius: 18 }}>
            <p style={{ fontSize: 18, marginBottom: 8 }}>No {connectMeetLabel.toLowerCase()} assignments yet</p>
            <p style={{ fontSize: 14, margin: 0 }}>Generate groups from the event detail page first.</p>
          </div>
        )}
      </div>
      {historyModal && (
        <Modal
          title={`${historyName} History`}
          body={<GroupHistory data={historyData} />}
          result={() => {
            setHistoryModal(false);
            setHistoryData(null);
          }}
        />
      )}
    </Layout>
  );
};

export default TroikaAssignments;
